import type {Collection, CollectionStateBase, Key} from '@react-types/shared';
import {useEffect, useRef} from 'react';
import type {TabListProps} from '@react-types/tabs';
import type {SingleSelectListState} from '../../@react-stately/list';
import {useSingleSelectListState} from '../../@react-stately/list';

export interface TabListStateOptions<T>
  extends Omit<TabListProps<T>, 'children'>,
    CollectionStateBase<T> {}

export interface TabListState<T> extends SingleSelectListState<T> {
  /** Whether the tab list is disabled. */
  isDisabled: boolean;
}

function findDefaultSelectedKey<T>(
  collection: Collection<T> | undefined,
  disabledKeys: Set<Key>,
) {
  let selectedKey = null;
  if (collection) {
    selectedKey = collection.getFirstKey();
    // loop over tabs until we find one that isn't disabled and select that
    while (
      selectedKey != null &&
      disabledKeys.has(selectedKey) &&
      selectedKey !== collection.getLastKey()
    ) {
      selectedKey = collection.getKeyAfter(selectedKey);
    }
    // if this check is true, then every item is disabled, it makes more sense to default to the first key than the last
    if (
      selectedKey != null &&
      disabledKeys.has(selectedKey) &&
      selectedKey === collection.getLastKey()
    ) {
      selectedKey = collection.getFirstKey();
    }
  }

  return selectedKey;
}

/**
 * Provides state management for a Tabs component. Tabs include a TabList which tracks
 * which tab is currently selected and displays the content associated with that Tab in a TabPanel.
 */
export function useTabListState<T extends object>(
  props: TabListStateOptions<T>,
): TabListState<T> {
  const state = useSingleSelectListState<T>({
    ...props,
    suppressTextValueWarning: true,
    defaultSelectedKey:
      props.defaultSelectedKey ??
      findDefaultSelectedKey(
        props.collection,
        props.disabledKeys ? new Set(props.disabledKeys) : new Set(),
      ) ??
      undefined,
  });

  const {selectionManager, collection, selectedKey: currentSelectedKey} = state;

  const lastSelectedKey = useRef<Key | null>(currentSelectedKey);
  useEffect(() => {
    // Ensure a tab is always selected (in case no selected key was specified or if selected item was deleted from collection)
    let selectedKey: Key | null = currentSelectedKey;
    if (selectionManager.isEmpty || !collection.getItem(selectedKey)) {
      selectedKey = findDefaultSelectedKey(collection, state.disabledKeys);
      if (selectedKey != null) {
        // directly set selection because replace/toggle selection won't consider disabled keys
        selectionManager.setSelectedKeys([selectedKey]);
      }
    }

    // If the tablist doesn't have focus and the selected key changes or if there isn't a focused key yet, change focused key to the selected key if it exists.
    if (
      (selectedKey != null && selectionManager.focusedKey == null) ||
      (!selectionManager.isFocused && selectedKey !== lastSelectedKey.current)
    ) {
      selectionManager.setFocusedKey(selectedKey);
    }
    lastSelectedKey.current = selectedKey;
  });

  return {
    ...state,
    isDisabled: props.isDisabled || false,
  };
}
